import React, { useState, useEffect } from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { Box, Button, FormControl, FormLabel, formLabelClasses, Grid, Input, Option, Select, Stack, Typography } from '@mui/joy';
import clientApi from '../api/client';
import { IClient } from '../interface/IClient';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../helper/ToastProvider';

type Props = {};

const ClientCreate = (props: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientState, setClientState] = useState<Partial<IClient>>();
  const { showToast } = useToast();

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const query = new URLSearchParams(location.search);
    const _clientId = query.get('client');
    if (_clientId) {
      setClientId(_clientId);
      try {
        const client = (await clientApi.getClient(Number(_clientId))).data;
        if (!client) {
          return navigate({ pathname: '/clients' });
        }
        let { deleted, ...temp } = client;
        setClientState(temp);
      } catch (e) {
        return navigate({ pathname: '/clients' });
      }
    }
  };

  return (
    <>
      <Breadcrumb crumb={['Clients', clientId ? 'Update' : 'Create']} />
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level='h2' component='h1'>
          {clientId ? 'Update Client' : 'Create Client'}
        </Typography>
      </Box>
      <Box
        component='main'
        sx={{
          py: 2,
          pb: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: 900,
          maxWidth: '100%',
          borderRadius: 'sm',
          '& form': {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          },
          [`& .${formLabelClasses.asterisk}`]: {
            visibility: 'hidden',
          },
        }}
      >
        <Stack gap={4} sx={{ mt: 2 }}>
          <form
            onSubmit={async (event: React.FormEvent<any>) => {
              event.preventDefault();
              try {
                const formData = new FormData(event.currentTarget);
                const formJson: IClient = Object.fromEntries((formData as any).entries()) as IClient;
                if (clientId) {
                  await clientApi.updateClient(Number(clientId), formJson);
                  showToast({
                    message: 'Client Update Success',
                    severity: 'success',
                  });
                } else {
                  await clientApi.createClient(formJson);
                  showToast({
                    message: 'Client Create Success',
                    severity: 'success',
                  });
                }
                navigate('/clients');
              } catch (e) {
                console.error(e);
                showToast({
                  message: 'Client Create/Update Failed',
                  severity: 'danger',
                });
              }
            }}
          >
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
              <Grid xs={12} md={6}>
                <FormControl required>
                  <FormLabel>Company Name</FormLabel>
                  <Input
                    type='text'
                    name='company'
                    value={clientState?.company || ''}
                    onChange={(e) => setClientState((prev) => ({ ...prev, company: e.target.value as string }))}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} md={6}>
                <FormControl required>
                  <FormLabel>Street</FormLabel>
                  <Input
                    type='text'
                    name='street'
                    value={clientState?.street || ''}
                    onChange={(e) => setClientState((prev) => ({ ...prev, street: e.target.value as string }))}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} md={6}>
                <FormControl required>
                  <FormLabel>Department</FormLabel>
                  <Input
                    type='text'
                    name='department'
                    value={clientState?.department || ''}
                    onChange={(e) => setClientState((prev) => ({ ...prev, department: e.target.value as string }))}
                  />
                </FormControl>
              </Grid>
              <Grid container spacing={1} xs={12} md={6}>
                <Grid xs={6} md={6}>
                  <FormControl required>
                    <FormLabel>ZIP</FormLabel>
                    <Input
                      type='text'
                      name='zip'
                      value={clientState?.zip || ''}
                      onChange={(e) => setClientState((prev) => ({ ...prev, zip: e.target.value as string }))}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={6} md={6}>
                  <FormControl required>
                    <FormLabel>City</FormLabel>
                    <Input
                      type='text'
                      name='city'
                      value={clientState?.city || ''}
                      onChange={(e) => setClientState((prev) => ({ ...prev, city: e.target.value as string }))}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={1} xs={12} md={6}>
                <Grid xs={12} md={6}>
                  <FormControl required>
                    <FormLabel>First Name</FormLabel>
                    <Input
                      type='text'
                      name='firstname'
                      value={clientState?.firstname || ''}
                      onChange={(e) => setClientState((prev) => ({ ...prev, firstname: e.target.value as string }))}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} md={6}>
                  <FormControl required>
                    <FormLabel>Last Name</FormLabel>
                    <Input
                      type='text'
                      name='lastname'
                      value={clientState?.lastname || ''}
                      onChange={(e) => setClientState((prev) => ({ ...prev, lastname: e.target.value as string }))}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid xs={12} md={6}>
                <FormControl required>
                  <FormLabel>M/W/D</FormLabel>
                  <Select
                    name='gender'
                    value={clientState?.gender || ''}
                    onChange={(e, value) => {
                      setClientState((prev) => ({ ...prev, gender: value as string }));
                    }}
                  >
                    <Option value='m'>Man</Option>
                    <Option value='w'>Woman</Option>
                    <Option value='d'>Diverse</Option>
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} md={6}>
                <FormControl required>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type='text'
                    name='email'
                    value={clientState?.email || ''}
                    onChange={(e) => setClientState((prev) => ({ ...prev, email: e.target.value as string }))}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Stack gap={4} sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Button type='submit' sx={{ marginRight: 2 }}>
                  {clientId ? 'Update' : 'Create'}
                </Button>
                <Button onClick={() => navigate('/clients')}>Cancel</Button>
              </Box>
            </Stack>
          </form>
        </Stack>
      </Box>
    </>
  );
};

export default ClientCreate;
